(function () {
  if (customElements.get('video-reels')) return;

  class VideoReels extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.video-reels__track');
      this.prevButton = this.querySelector('.video-reels__arrow--prev');
      this.nextButton = this.querySelector('.video-reels__arrow--next');
      this.cards = Array.from(this.querySelectorAll('.video-reels__card'));
      this.visibility = new Map();
      this.soundCard = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayEnabled = this.hasAttribute('data-autoplay-in-view') && !this.reducedMotion;

      this.querySelectorAll('.video-reels__play').forEach((button) => {
        button.addEventListener('click', () => this.togglePlay(button.closest('.video-reels__card')));
      });

      if (this.prevButton) this.prevButton.addEventListener('click', () => this.scrollByCards(-1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.scrollByCards(1));
      if (this.track) {
        this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
        window.addEventListener('resize', () => this.updateArrows());
        this.updateArrows();
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => this.onVisibilityChange(entries), {
          threshold: [0, 0.25, 0.5, 0.75, 0.95],
        });
        this.cards.forEach((card) => this.observer.observe(card));
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    onVisibilityChange(entries) {
      entries.forEach((entry) => this.visibility.set(entry.target, entry.intersectionRatio));

      // Pause anything that scrolled (mostly) out of view, including sound playback.
      this.cards.forEach((card) => {
        const ratio = this.visibility.get(card) || 0;
        const video = card.querySelector('video');
        if (video && !video.paused && ratio < 0.4) {
          this.pauseCard(card);
          if (this.soundCard === card) this.soundCard = null;
        }
      });

      if (!this.autoplayEnabled || this.soundCard) return;

      // Autoplay exactly one video: the most visible card that has a video.
      let best = null;
      let bestRatio = 0.7; // must be at least 70% on screen
      this.cards.forEach((card) => {
        const ratio = this.visibility.get(card) || 0;
        if (ratio >= bestRatio && card.querySelector('video')) {
          best = card;
          bestRatio = ratio === bestRatio && best !== card ? bestRatio : ratio;
        }
      });

      if (best) {
        const video = best.querySelector('video');
        if (video.paused) {
          this.cards.forEach((card) => {
            if (card !== best) this.pauseCard(card);
          });
          video.muted = true;
          video.play().catch(() => {});
          best.classList.add('is-playing');
        }
      }
    }

    pauseCard(card) {
      const video = card.querySelector('video');
      if (video && !video.paused) video.pause();
      card.classList.remove('is-playing');
    }

    togglePlay(card) {
      if (!card) return;
      const video = card.querySelector('video');
      if (!video) return;

      const playingWithSound = !video.paused && !video.muted;
      if (playingWithSound) {
        this.pauseCard(card);
        this.soundCard = null;
        return;
      }

      // Play this card with sound; everything else stops.
      this.cards.forEach((other) => {
        if (other !== card) this.pauseCard(other);
      });
      video.muted = false;
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
      card.classList.add('is-playing');
      this.soundCard = card;
      video.addEventListener(
        'ended',
        () => {
          card.classList.remove('is-playing');
          if (this.soundCard === card) this.soundCard = null;
        },
        { once: true }
      );
    }

    scrollByCards(direction) {
      if (!this.track) return;
      const card = this.track.querySelector('.video-reels__card');
      if (!card) return;
      const gap = 12;
      this.track.scrollBy({
        left: direction * (card.offsetWidth + gap) * 2,
        behavior: this.reducedMotion ? 'auto' : 'smooth',
      });
    }

    updateArrows() {
      if (!this.track) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prevButton) this.prevButton.toggleAttribute('disabled', this.track.scrollLeft <= 2);
      if (this.nextButton) this.nextButton.toggleAttribute('disabled', this.track.scrollLeft >= maxScroll);
    }
  }

  customElements.define('video-reels', VideoReels);
})();
